"use client";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { COVERAGE_AREA_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useGetCoverageAreas from "@/hooks/requests/coverage-area/use-get-coverage-areas";
import useImportCoverageAreas from "@/hooks/requests/coverage-area/use-import-coverage-areas";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useFilters from "@/hooks/use-filters";
import useMutate from "@/hooks/use-mutate";
import usePagination from "@/hooks/use-pagination";
import ICoverageArea from "@/interfaces/coverage-area.interface";
import { CoverageAreaMessages } from "@/messages/coverage-area/coverage-area.messages";
import { FilterType, IFilter } from "@/types/filter.types";

import FiltersForm from "./components/FiltersForm";
import { CoverageAreaTable } from "./components/Table";
import { initialFilterValues } from "./constants";
import { CoverageAreaFilter } from "./types";

type Props = {
  messages: CoverageAreaMessages;
};

const FIRST_PAGE = 0;
const DEFAULT_CUSTOMER_ID = 1;
const LOADING_SPINNER_SIZE = 50;
const CARD_MARGIN_TOP = 16;

function CoverageAreaPage({ messages }: Props) {
  const pagination = usePagination();
  const router = useNavigate();
  const { pageNumber, pageSize, changePage } = pagination;
  const { filters, changeFilters } = useFilters<ICoverageArea>({
    initialValue: initialFilterValues as IFilter<ICoverageArea>[],
  });
  const coverageAreaFilters = filters as CoverageAreaFilter[];
  const { lang } = useLanguage();
  const { mutate } = useMutate();

  const getCoverageAreas = useGetCoverageAreas({ filters: coverageAreaFilters, pageNumber, pageSize });
  // Check if there are any coverage areas in database (without filters)
  const emptyFilters = [
    { property: "areaDescription", value: "", filterType: FilterType.Contains },
  ] as CoverageAreaFilter[];
  const getCoverageAreasTotal = useGetCoverageAreas({
    filters: emptyFilters,
    pageNumber: 0,
    pageSize: 1,
  });
  const { importAreas, isLoading: isImporting } = useImportCoverageAreas();
  const { data, isLoading, error } = getCoverageAreas;
  const { data: totalData } = getCoverageAreasTotal;
  const hasNoPermissionToCreate = useHasNoPermission(COVERAGE_AREA_PERMISSIONS.CREATE);

  const shouldLoadTable = !!data && !isLoading && !error && data.items.length > 0;
  const shouldLoadError = !data && !isLoading && !!error;
  const shouldLoadNoFound = !!data && !isLoading && !error && data.items.length === 0;

  // Check if database is completely empty (no coverage areas at all)
  const hasCoverageAreasInDatabase = !!(totalData && totalData.items.length > 0);
  const shouldShowImportButton = !hasCoverageAreasInDatabase;

  async function handleImportCoverageAreas() {
    const successMessage = messages.loadCoverageAreasSuccess;
    const errorMessage = messages.loadCoverageAreasError;
    const formData = { customerId: DEFAULT_CUSTOMER_ID };
    await importAreas({ formData, successMessage, errorMessage });
    mutate(SWR_KEYS.getCoverageAreas);
  }

  return (
    <PageLayout messages={messages} title={messages.pageTitle}>
      <PageContainer>
        <Formik
          initialValues={coverageAreaFilters}
          validateOnChange={false}
          onSubmit={(submittedFilters) => {
            changePage(FIRST_PAGE);
            changeFilters(submittedFilters as IFilter<ICoverageArea>[]);
          }}
        >
          <FiltersForm messages={messages} />
        </Formik>
        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(CARD_MARGIN_TOP) })}>
          <Suspense
            isSuspended={isLoading}
            fallback={<CircularProgress size={LOADING_SPINNER_SIZE} />}
          >
            <MDBox p={2}>
              <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="bold">
                  {messages.coverageAreaTableTitle}
                </MDTypography>

                {shouldShowImportButton ? (
                  <MDBox mt={2} display="flex" justifyContent="center">
                    <MDButton
                      variant="gradient"
                      color="dark"
                      onClick={handleImportCoverageAreas}
                      isLoading={isImporting}
                    >
                      {messages.importCoverageAreasButton}
                    </MDButton>
                  </MDBox>
                ) : (
                  <MDButton
                    variant="gradient"
                    color="dark"
                    endIcon={<AddCircleOutlineIcon />}
                    onClick={() => router(ROUTE_URLS.createCoverageArea(lang))}
                    disabled={hasNoPermissionToCreate}
                  >
                    {messages.addCoverageAreaButton}
                  </MDButton>
                )}
              </MDBox>

              {!!shouldLoadTable && (
                <CoverageAreaTable pagination={pagination} data={data} messages={messages} />
              )}

              {!!shouldLoadError && (
                <AlertBlock
                  title={messages.coverageAreaErrorToLoad}
                  description={error.message}
                  alt={messages.coverageAreaNotFoundAlt}
                />
              )}

              {!!shouldLoadNoFound && (
                <AlertBlock
                  title={messages.coverageAreaNotFoundError}
                  description={messages.coverageAreaNotFoundDescription}
                  alt={messages.coverageAreaNotFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}

export default CoverageAreaPage;
